import type { TileState } from '@familyhub/shared';

/**
 * The on-screen keyboard — a Dutch QWERTY (Dutch keyboards are QWERTY), each
 * letter coloured by the best state it has reached across all guesses, so it
 * doubles as a record of what has been tried.
 *
 * The bottom row is laid out for a thumb and for mobile muscle memory: **Wis**
 * (backspace) sits bottom-left and **Enter** bottom-right, the two of them
 * carrying text labels rather than icons and each 1.5× the width of a letter
 * key so they are hard to mis-hit and never read as a letter. The physical
 * keyboard is wired separately in the screen; this is the same set of actions
 * for a thumb.
 */
const ROWS = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'] as const;

export function Keyboard({
  letterStates,
  onKey,
  onEnter,
  onBackspace,
  disabled,
}: {
  letterStates: Record<string, TileState>;
  onKey: (letter: string) => void;
  onEnter: () => void;
  onBackspace: () => void;
  disabled: boolean;
}) {
  const lastRow = ROWS.length - 1;
  return (
    <div className="wl-keyboard" aria-label="Toetsenbord">
      {ROWS.map((row, i) => (
        <div className="wl-krow" key={row}>
          {i === lastRow && (
            <button
              type="button"
              className="wl-key wl-key-action wl-key-delete"
              onClick={onBackspace}
              disabled={disabled}
            >
              Wis
            </button>
          )}
          {[...row].map((letter) => (
            <button
              type="button"
              key={letter}
              className="wl-key"
              data-state={letterStates[letter]}
              onClick={() => onKey(letter)}
              disabled={disabled}
              aria-label={`Letter ${letter}`}
            >
              {letter}
            </button>
          ))}
          {i === lastRow && (
            <button
              type="button"
              className="wl-key wl-key-action wl-key-enter"
              onClick={onEnter}
              disabled={disabled}
            >
              Enter
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
